import type { DatasetMode, WorkflowDetail, WorkflowText } from "../types";
import { stepTarget } from "../utils/textTarget";
import { ModeSettingsTable } from "./ModeSettingsTable";
import { StatusTag } from "./StatusTag";
import { WorkflowExample } from "./WorkflowExample";
import { WorkflowSteps } from "./WorkflowSteps";
import type { TextTarget } from "./WorkflowTextDialog";

const CHIP =
  "rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-zinc-400 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400";

type Props = {
  detail: WorkflowDetail;
  modes: DatasetMode[];
  onOpen: (target: TextTarget) => void;
};

const stepsIntro = (detail: WorkflowDetail): string => {
  if (detail.single_skill) {
    return `The agent gets the whole skill ${detail.single_skill} at once. Each step is one of its sections. Open a step to read it as the agent gets it.`;
  }
  if (detail.status === "archived") {
    return "Open a step to read the earlier text. The agent no longer gets these texts.";
  }
  return detail.status === "planned"
    ? "Open a step to read its planned text. The agent does not get these texts yet."
    : "Open a step to read the text that the agent loads for it.";
};

const otherTextsIntro: Record<WorkflowDetail["status"], string> = {
  "in-use": "The agent reads these when they apply, for example after an interruption.",
  planned: "The texts that this design builds on.",
  archived: "The other texts of the earlier version.",
};

const textTarget = (detail: WorkflowDetail, text: WorkflowText): TextTarget => ({
  title: text.label,
  description: `A text of the ${detail.label} workflow.`,
  source: text.source,
  markdown: text.markdown,
  planned: text.planned,
  archived: text.archived,
});

/** One workflow: what it is for, an example, its steps, its modes, and its other texts. */
export const WorkflowSection = ({ detail, modes, onOpen }: Props) => {
  const planned = detail.status === "planned";
  return (
    <section aria-labelledby="workflow-title" className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="workflow-title" className="text-xl font-semibold text-zinc-900">
              {detail.label} workflow
            </h2>
            <StatusTag status={detail.status} />
          </div>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-zinc-600">{detail.description}</p>
          {detail.availability && (
            <p className="mt-1 max-w-3xl text-sm leading-6 text-zinc-600">
              {detail.availability}
            </p>
          )}
          <p className="mt-1 font-mono text-2xs text-zinc-500">
            {detail.name} · {detail.step_count} steps
          </p>
        </div>
        <button
          type="button"
          className={CHIP}
          onClick={() =>
            onOpen({
              title: detail.label,
              description: "The introduction of the workflow, before its steps.",
              markdown: detail.overview_markdown,
              planned,
              archived: detail.status === "archived",
            })
          }
        >
          Read the introduction
        </button>
      </div>

      <WorkflowExample detail={detail} />

      <section aria-labelledby="workflow-steps">
        <h3 id="workflow-steps" className="text-base font-semibold text-zinc-900">
          Steps
        </h3>
        <p className="mb-4 mt-1 max-w-3xl text-sm leading-6 text-zinc-600">
          {stepsIntro(detail)}
        </p>
        <WorkflowSteps detail={detail} onOpen={(step) => onOpen(stepTarget(detail, step))} />
      </section>

      {modes.length > 0 && <ModeSettingsTable modes={modes} />}

      {detail.other_texts.length > 0 && (
        <section aria-labelledby="workflow-other-texts">
          <h3 id="workflow-other-texts" className="text-base font-semibold text-zinc-900">
            Other texts
          </h3>
          <p className="mt-1 text-sm text-zinc-600">
            {otherTextsIntro[detail.status]}
          </p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {detail.other_texts.map((text) => (
              <li key={text.label}>
                <button
                  type="button"
                  className={CHIP}
                  onClick={() => onOpen(textTarget(detail, text))}
                >
                  {text.label}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
};
